import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  getHouseScores, 
  saveHouseScores,
  getHouseMedals, 
  getSportsEvents, 
  getCustomHouses,
  getOrganizerName,
  saveOrganizerName,
  getActiveSportEvent,
  getBarometerPointsLimit,
  saveBarometerPointsLimit,
  announceInterHouseChampion,
  getInterHouseChampions
} from '../utils/quizData';
import { playWinFanfare, playTickSound } from '../utils/soundEffects';
import { useGameExitRoute } from '../utils/navigation';

const { 
  FiMaximize, 
  FiMinimize, 
  FiArrowLeft, 
  FiVolume2, 
  FiVolumeX, 
  FiAward, 
  FiTv, 
  FiInfo, 
  FiCheckCircle, 
  FiX, 
  FiPlay, 
  FiTarget, 
  FiUsers, 
  FiCompass,
  FiSun,
  FiMoon,
  FiEdit3,
  FiSave
} = FiIcons;

function LiveHouseBarometerStageComponent() {
  const { exitRoute, exitTitle } = useGameExitRoute();
  const [houseScores, setHouseScores] = useState(getHouseScores());
  const [houseMedals, setHouseMedals] = useState(getHouseMedals());
  const [sportsEvents, setSportsEvents] = useState(getSportsEvents());
  const [houses, setHouses] = useState(getCustomHouses());
  const [organizerName, setOrganizerName] = useState(getOrganizerName());
  const [activeSportEvent, setActiveSportEvent] = useState(getActiveSportEvent());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [celebrationHouse, setCelebrationHouse] = useState(null);
  const [showRulesModal, setShowRulesModal] = useState(false);

  // Points Gain Limit for Championship Award Trigger
  const [pointsLimit, setPointsLimit] = useState(getBarometerPointsLimit());
  const [isEditingPointsLimit, setIsEditingPointsLimit] = useState(false);
  const [customPointsInput, setCustomPointsInput] = useState('');

  // Inter-House Champion Announcement Modal
  const [showChampionModal, setShowChampionModal] = useState(false);
  const [selectedChampionHouseId, setSelectedChampionHouseId] = useState(null);
  const [awardType, setAwardType] = useState('🏆 Overall Annual Inter-House Championship Trophy');
  const [customAward, setCustomAward] = useState('');
  const [prizeDistributionInfo, setPrizeDistributionInfo] = useState('Trophy, Gold Medals & Certificates presented to winning House on Stage');
  const [announcedBy, setAnnouncedBy] = useState('Chief Sports Referee / Principal');
  const [eventSource, setEventSource] = useState('Official Inter-House Championship 2026');
  const [announcedSuccessBanner, setAnnouncedSuccessBanner] = useState(null);

  // Day & Night Mode (defaults to 'day' for lighter colors and distant visibility)
  const [stageTheme, setStageTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('house_barometer_theme') || 'day';
    }
    return 'day';
  });

  // Inline School Name Edit Modal
  const [isEditingSchoolName, setIsEditingSchoolName] = useState(false);
  const [schoolNameInput, setSchoolNameInput] = useState(organizerName);

  // Sync listener across windows, tabs, and local storage events
  useEffect(() => {
    const handleUpdate = () => {
      const nextScores = getHouseScores();
      const nextEvents = getSportsEvents();
      const currentActive = getActiveSportEvent();

      // Trigger fanfare if latest event is 1st place
      if (nextEvents.length > sportsEvents.length && soundEnabled) {
        const latest = nextEvents[0];
        if (latest && latest.place === '1st') {
          playWinFanfare();
          setCelebrationHouse(latest.houseId);
          setTimeout(() => setCelebrationHouse(null), 4000);
        }
      }

      setHouseScores(nextScores);
      setHouseMedals(getHouseMedals());
      setSportsEvents(nextEvents);
      setHouses(getCustomHouses());
      const nextOrg = getOrganizerName();
      setOrganizerName(nextOrg);
      setSchoolNameInput(nextOrg);
      setActiveSportEvent(currentActive);

      // If active event requested projector rules display
      if (currentActive && currentActive.showRulesOnProjector) {
        setShowRulesModal(true);
      }
    };

    window.addEventListener('houseScoresUpdated', handleUpdate);
    window.addEventListener('sportsEventsUpdated', handleUpdate);
    window.addEventListener('houseMedalsUpdated', handleUpdate);
    window.addEventListener('customHousesUpdated', handleUpdate);
    window.addEventListener('activeSportEventUpdated', handleUpdate);
    window.addEventListener('organizerNameUpdated', handleUpdate);
    window.addEventListener('barometerPointsLimitUpdated', handleUpdate);
    window.addEventListener('storage', handleUpdate);

    return () => {
      window.removeEventListener('houseScoresUpdated', handleUpdate);
      window.removeEventListener('sportsEventsUpdated', handleUpdate);
      window.removeEventListener('houseMedalsUpdated', handleUpdate);
      window.removeEventListener('customHousesUpdated', handleUpdate);
      window.removeEventListener('activeSportEventUpdated', handleUpdate);
      window.removeEventListener('organizerNameUpdated', handleUpdate);
      window.removeEventListener('barometerPointsLimitUpdated', handleUpdate);
      window.removeEventListener('storage', handleUpdate);
    };
  }, [sportsEvents, soundEnabled]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const toggleStageTheme = () => {
    const nextTheme = stageTheme === 'day' ? 'night' : 'day';
    setStageTheme(nextTheme);
    localStorage.setItem('house_barometer_theme', nextTheme);
  };

  const handleSaveSchoolName = (e) => {
    e?.preventDefault();
    if (schoolNameInput.trim()) {
      saveOrganizerName(schoolNameInput.trim());
      setOrganizerName(schoolNameInput.trim());
      setIsEditingSchoolName(false);
    }
  };

  const handleUpdatePointsLimit = (newLimit) => {
    const val = parseInt(newLimit, 10);
    if (!isNaN(val) && val > 0) {
      saveBarometerPointsLimit(val);
      setPointsLimit(val);
      setIsEditingPointsLimit(false);
      setCustomPointsInput('');
      playTickSound(880, 0.08);
    }
  };

  const handleAnnounceChampion = () => {
    const champHouse = safeHouses.find(h => h.id === selectedChampionHouseId) || sortedHouses[0];
    if (!champHouse) return;

    const chosenAward = awardType === 'CUSTOM' ? (customAward.trim() || 'Official Inter-House Trophy') : awardType;
    const finalPoints = safeScores[champHouse.id] || 0;

    const record = announceInterHouseChampion({
      houseId: champHouse.id,
      houseName: champHouse.name,
      badge: champHouse.badge,
      points: finalPoints,
      awardType: chosenAward,
      eventSource: eventSource.trim() || 'Official Inter-House Championship',
      prizeDistributionInfo: prizeDistributionInfo.trim() || 'Trophy, Medals & Certificates awarded to winning House on Stage',
      announcedBy: announcedBy.trim() || 'Chief Sports Referee / Principal'
    });

    if (soundEnabled) {
      playWinFanfare();
    }
    setCelebrationHouse(champHouse.id);
    setAnnouncedSuccessBanner(record);
    setShowChampionModal(false);
  };

  const safeScores = (houseScores && typeof houseScores === 'object') ? houseScores : { red: 0, blue: 0, green: 0, yellow: 0 };
  const safeHouses = (Array.isArray(houses) && houses.length > 0) ? houses : [
    { id: 'red', name: 'Ruby Lions', house: 'Red House', badge: '🦁' },
    { id: 'blue', name: 'Sapphire Eagles', house: 'Blue House', badge: '🦅' },
    { id: 'green', name: 'Emerald Tigers', house: 'Green House', badge: '🐯' },
    { id: 'yellow', name: 'Golden Falcons', house: 'Yellow House', badge: '⚡' }
  ];
  const safeMedals = (houseMedals && typeof houseMedals === 'object') ? houseMedals : {};
  const safeEvents = Array.isArray(sportsEvents) ? sportsEvents : [];

  const sortedHouses = [...safeHouses].sort((a, b) => (safeScores[b?.id] || 0) - (safeScores[a?.id] || 0));
  const maxScore = Math.max(...Object.values(safeScores).map(v => Number(v) || 0), 120);
  const handleScoreBump = (houseId, delta) => {
    const cur = safeScores[houseId] || 0;
    const next = Math.max(0, cur + delta);
    const updated = { ...safeScores, [houseId]: next };
    setHouseScores(updated);
    saveHouseScores(updated);
    playTickSound(delta > 0 ? 880 : 440, 0.05);
  };

  const latestEvent = safeEvents[0] || null;
  const isDay = stageTheme === 'day';

  return (
    <div className={`min-h-screen font-sans flex flex-col justify-between overflow-x-hidden overflow-y-auto relative select-none transition-colors duration-500 ${
      isDay ? 'bg-slate-100 text-slate-900' : 'bg-slate-950 text-white'
    }`}>
      
      {/* Background Stadium Glow / Daylight Gradients */}
      {isDay ? (
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-sky-100/80 via-slate-100 to-indigo-100/40 pointer-events-none"></div>
      ) : (
        <>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-indigo-900/40 via-slate-950 to-slate-950 pointer-events-none"></div>
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none"></div>
          <div className="absolute top-0 right-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl pointer-events-none"></div>
        </>
      )}

      {/* TOP STAGE HEADER WITH LARGE SCHOOL ORGANIZER BRANDING */}
      <header className={`relative z-10 px-6 py-3.5 flex items-center justify-between border-b backdrop-blur-md transition-colors ${
        isDay 
          ? 'bg-white/95 border-slate-200/90 shadow-sm' 
          : 'bg-slate-950/75 border-white/10'
      }`}>
        <div className="flex items-center gap-4">
          <Link
            to={exitRoute}
            className={`p-2.5 rounded-xl border transition-colors ${
              isDay 
                ? 'bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-700' 
                : 'bg-white/5 hover:bg-white/10 border-white/10 text-slate-400 hover:text-white'
            }`}
            title={exitTitle}
          >
            <SafeIcon icon={FiArrowLeft} className="text-lg" />
          </Link>
          
          {/* High-Visibility School / Event Organizer Display */}
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-ping"></span>
              <span className={`text-[11px] font-black tracking-widest uppercase ${
                isDay ? 'text-indigo-600' : 'text-amber-400'
              }`}>
                OFFICIAL INTER-HOUSE CHAMPIONSHIP
              </span>
            </div>
            
            {/* LARGE SCHOOL NAME (Visible from great distance across auditorium) */}
            <div className="flex items-center gap-3">
              <h1 className={`text-xl sm:text-2xl md:text-3xl font-black tracking-tight flex items-center gap-2 drop-shadow-sm ${
                isDay ? 'text-indigo-950' : 'text-amber-300'
              }`}>
                <span>🏫 {organizerName}</span>
              </h1>
              
              <button
                type="button"
                onClick={() => {
                  setSchoolNameInput(organizerName);
                  setIsEditingSchoolName(true);
                }}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all flex items-center gap-1 shadow-sm ${
                  isDay
                    ? 'bg-slate-200 hover:bg-indigo-100 text-indigo-700 border border-slate-300'
                    : 'bg-white/10 hover:bg-amber-400/20 text-amber-300 border border-white/15'
                }`}
                title="Click to edit school or organizer name"
              >
                <SafeIcon icon={FiEdit3} className="text-xs" />
                <span className="text-[11px] font-bold">Edit Name</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stage Controls: Target Limit, Announce Champion, Day/Night Mode, Rules, Fullscreen */}
        <div className="flex items-center gap-2 flex-wrap justify-end">
          {/* POINTS GAIN LIMIT CONTROLLER & PRESET SELECTOR */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setIsEditingPointsLimit(!isEditingPointsLimit)}
              className={`px-3 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-1.5 shadow-sm border ${
                isDay
                  ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-emerald-300'
                  : 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border-emerald-500/40'
              }`}
              title="Set Points Gain Limit to announce Inter-House Champion"
            >
              <SafeIcon icon={FiTarget} className="text-emerald-500 text-sm" />
              <span>Target: <strong className="font-mono">{pointsLimit}</strong> Pts</span>
            </button>

            {/* Dropdown for Points Gain Limit */}
            <AnimatePresence>
              {isEditingPointsLimit && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 5 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 5 }}
                  className="absolute right-0 top-full mt-2 w-56 p-3 rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl z-50 text-slate-800 dark:text-white"
                >
                  <div className="text-[11px] font-black uppercase text-slate-400 mb-2 flex items-center justify-between">
                    <span>Points Gain Limit</span>
                    <button onClick={() => setIsEditingPointsLimit(false)} className="text-slate-400 hover:text-slate-600">
                      <SafeIcon icon={FiX} />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-1.5 mb-2.5">
                    {[50, 100, 200, 300, 500, 1000].map(val => (
                      <button
                        key={val}
                        type="button"
                        onClick={() => handleUpdatePointsLimit(val)}
                        className={`py-1.5 px-2 rounded-lg text-xs font-black transition-all ${
                          pointsLimit === val
                            ? 'bg-emerald-500 text-slate-950 font-black shadow-sm'
                            : 'bg-slate-100 dark:bg-slate-800 hover:bg-emerald-100 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200'
                        }`}
                      >
                        {val} Pts
                      </button>
                    ))}
                  </div>

                  <form 
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdatePointsLimit(customPointsInput);
                    }}
                    className="flex gap-1.5"
                  >
                    <input
                      type="number"
                      placeholder="Custom..."
                      value={customPointsInput}
                      onChange={e => setCustomPointsInput(e.target.value)}
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-lg px-2 py-1 text-xs outline-none"
                    />
                    <button
                      type="submit"
                      className="px-2.5 py-1 bg-emerald-500 text-slate-950 rounded-lg text-xs font-black shrink-0 hover:bg-emerald-400"
                    >
                      Set
                    </button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ANNOUNCE INTER-HOUSE CHAMPION BUTTON */}
          {(() => {
            const hasQualified = sortedHouses.some(h => (safeScores[h.id] || 0) >= pointsLimit);
            return (
              <button
                type="button"
                onClick={() => {
                  setSelectedChampionHouseId(sortedHouses[0]?.id || 'red');
                  setShowChampionModal(true);
                }}
                className={`px-3.5 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-1.5 shadow-lg active:scale-95 cursor-pointer ${
                  hasQualified
                    ? 'bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-500 text-slate-950 shadow-amber-500/40 ring-2 ring-amber-300 animate-pulse'
                    : isDay
                    ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300'
                    : 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-300 border border-amber-500/40'
                }`}
                title="Announce Inter-House Champion & Award"
              >
                <SafeIcon icon={FiAward} className="text-base text-amber-500 dark:text-amber-300" />
                <span className="font-black">Announce Champion 🏆</span>
              </button>
            );
          })()}

          {/* DAY / NIGHT MODE TOGGLE BUTTON */}
          <button
            type="button"
            onClick={toggleStageTheme}
            className={`px-3.5 py-2 rounded-xl font-black text-xs transition-all flex items-center gap-1.5 shadow-sm border ${
              isDay
                ? 'bg-amber-400/20 hover:bg-amber-400/30 text-amber-900 border-amber-300'
                : 'bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border-indigo-500/40'
            }`}
            title={isDay ? "Switch to Night Arena Mode" : "Switch to Day Stadium Mode"}
          >
            <SafeIcon icon={isDay ? FiMoon : FiSun} className="text-base text-amber-500" />
            <span className="hidden sm:inline font-bold">{isDay ? "Night Arena" : "Day Stadium"}</span>
          </button>

          {activeSportEvent && (
            <button
              type="button"
              onClick={() => setShowRulesModal(true)}
              className={`px-3.5 py-2 rounded-xl font-black text-xs transition-colors flex items-center gap-1.5 shadow-sm border ${
                isDay
                  ? 'bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border-indigo-200'
                  : 'bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border-indigo-400/30'
              }`}
              title="Show Game Rules on Screen"
            >
              <SafeIcon icon={FiInfo} className="text-sm" />
              <span>Rules Briefing</span>
            </button>
          )}

          <button
            type="button"
            onClick={() => setSoundEnabled(!soundEnabled)}
            className={`p-2.5 rounded-xl border transition-colors ${
              isDay
                ? 'bg-slate-100 hover:bg-slate-200 border-slate-300 text-slate-700'
                : 'bg-white/5 hover:bg-white/10 border-white/10 text-slate-300 hover:text-white'
            }`}
            title={soundEnabled ? "Mute Fanfare Audio" : "Enable Audio"}
          >
            <SafeIcon icon={soundEnabled ? FiVolume2 : FiVolumeX} className="text-lg" />
          </button>

          <button
            type="button"
            onClick={toggleFullscreen}
            className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 font-black text-xs transition-transform active:scale-95 flex items-center gap-2 shadow-lg shadow-amber-500/20"
          >
            <SafeIcon icon={isFullscreen ? FiMinimize : FiMaximize} className="text-base" />
            <span className="hidden sm:inline">{isFullscreen ? "Exit Fullscreen" : "Projector View"}</span>
          </button>
        </div>
      </header>

      {/* CHAMPION OFFICIALLY CROWNED BANNER - STADIUM GRADE HIGH DISTANCE VISIBILITY */}
      <AnimatePresence>
        {announcedSuccessBanner && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.98 }}
            className="relative z-30 px-6 sm:px-8 py-4 sm:py-5 bg-gradient-to-r from-slate-950 via-indigo-950 to-slate-950 text-white shadow-2xl border-y-2 sm:border-y-4 border-amber-400 flex items-center justify-between gap-4 ring-4 ring-amber-400/20"
          >
            {/* Glowing gold ambient light */}
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 via-yellow-400/15 to-amber-500/10 pointer-events-none" />

            <div className="relative z-10 flex items-center gap-4 sm:gap-6 flex-wrap">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-yellow-500 text-slate-950 flex items-center justify-center text-3xl sm:text-4xl shadow-[0_0_25px_rgba(250,204,21,0.6)] shrink-0 animate-bounce">
                🏆
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
                  <span className="bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 px-3.5 py-1 rounded-full text-xs sm:text-sm font-black uppercase tracking-widest shadow-md flex items-center gap-1.5">
                    <span>👑</span>
                    <span>OFFICIAL CHAMPION ANNOUNCED</span>
                  </span>

                  <span className="text-xl sm:text-2xl md:text-3xl font-black text-white tracking-wide drop-shadow-md">
                    {announcedSuccessBanner.houseName}
                  </span>

                  <span className="bg-amber-400/20 text-amber-300 border-2 border-amber-400/60 px-3 py-0.5 rounded-xl text-sm sm:text-base font-black font-mono shadow-inner">
                    {announcedSuccessBanner.points} PTS
                  </span>

                  <span className="bg-emerald-500/25 text-emerald-300 border border-emerald-400/50 px-2.5 py-0.5 rounded-full text-[11px] sm:text-xs font-black uppercase tracking-wider">
                    ✓ Award Crowned
                  </span>
                </div>

                <div className="text-xs sm:text-sm md:text-base font-semibold text-slate-200 flex flex-wrap items-center gap-x-2 gap-y-1 pt-0.5">
                  <span className="text-amber-300 font-extrabold flex items-center gap-1">
                    <span>Award:</span>
                    <span className="underline decoration-amber-400 underline-offset-2">{announcedSuccessBanner.awardType}</span>
                  </span>
                  <span className="text-slate-400">&bull;</span>
                  <span className="text-white font-medium">
                    Prize Details: <span className="text-amber-200 italic font-semibold">{announcedSuccessBanner.prizeDistributionInfo}</span>
                  </span>
                  <span className="text-slate-400">&bull;</span>
                  <span className="text-indigo-300 font-bold bg-indigo-500/20 px-2 py-0.5 rounded-md border border-indigo-400/30 text-xs">
                    Recorded in Sports Day Log & Inter-House Champions menu
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setAnnouncedSuccessBanner(null)}
              className="relative z-10 p-2.5 rounded-2xl bg-white/10 hover:bg-white/20 text-white hover:text-amber-300 border border-white/20 hover:border-amber-400 transition-all cursor-pointer shadow-md shrink-0 hover:scale-110 active:scale-95"
              title="Close Announcement Banner"
            >
              <SafeIcon icon={FiX} className="text-xl sm:text-2xl" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TARGET THRESHOLD REACHED NOTIFICATION */}
      {(() => {
        const qualified = sortedHouses.filter(h => (safeScores[h.id] || 0) >= pointsLimit);
        if (qualified.length > 0 && !announcedSuccessBanner) {
          return (
            <div className="relative z-20 px-6 py-2.5 bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 border-b border-amber-400/40 text-white flex items-center justify-between gap-3 text-xs flex-wrap">
              <div className="flex items-center gap-2.5 flex-wrap">
                <span className="px-2.5 py-1 rounded-full bg-amber-400 text-slate-950 font-black text-[10px] uppercase tracking-wider animate-pulse flex items-center gap-1">
                  <span>🎯</span> TARGET REACHED
                </span>
                <span className="font-semibold text-slate-200">
                  <strong className="text-amber-300">{qualified.map(h => h.name).join(', ')}</strong> has achieved the <strong className="text-white">{pointsLimit} points</strong> championship limit! Ready to announce winners.
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedChampionHouseId(qualified[0]?.id || sortedHouses[0]?.id);
                  setShowChampionModal(true);
                }}
                className="px-4 py-1.5 rounded-xl bg-gradient-to-r from-amber-400 to-yellow-400 text-slate-950 font-black text-xs hover:brightness-110 active:scale-95 shadow-md flex items-center gap-1.5 cursor-pointer"
              >
                <span>Announce Award Now</span>
                <SafeIcon icon={FiAward} />
              </button>
            </div>
          );
        }
        return null;
      })()}

      {/* ACTIVE SPORT SCORING HERO BANNER (Displayed only when an active sport match is broadcasting) */}
      {activeSportEvent && (
        <div className={`relative z-10 px-6 py-2.5 border-b backdrop-blur-md transition-colors ${
          isDay
            ? 'bg-white border-slate-200 shadow-sm'
            : 'bg-gradient-to-r from-indigo-950/90 via-slate-900/90 to-indigo-950/90 border-indigo-500/20'
        }`}>
          <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-red-600 dark:text-red-300 font-black text-[11px] tracking-wider uppercase flex items-center gap-1.5 animate-pulse">
                <span className="w-2 h-2 rounded-full bg-red-500"></span>
                LIVE SCORING EVENT
              </span>

              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-xl sm:text-2xl font-black drop-shadow-sm ${
                  isDay ? 'text-indigo-950' : 'text-amber-300'
                }`}>
                  {activeSportEvent.sportName}
                </span>

                {activeSportEvent.category === 'Indoor Fun (<7 Years)' && (
                  <span className={`px-2.5 py-0.5 rounded-md text-[11px] font-black border ${
                    isDay 
                      ? 'bg-purple-100 border-purple-300 text-purple-700' 
                      : 'bg-purple-500/20 border-purple-400/30 text-purple-300'
                  }`}>
                    🎈 Under 7 Fun Games
                  </span>
                )}

                <span className={`px-2.5 py-0.5 rounded-md text-xs font-bold ${
                  isDay ? 'bg-slate-200 text-slate-700' : 'bg-white/10 text-slate-300'
                }`}>
                  {activeSportEvent.division || 'All Divisions'}
                </span>

                <span className={`px-2 py-0.5 rounded-md text-xs font-extrabold border ${
                  isDay
                    ? 'bg-amber-100 text-amber-800 border-amber-300'
                    : 'bg-amber-500/10 text-amber-300 border-amber-500/20'
                }`}>
                  +{activeSportEvent.points || 50} PTS AT STAKE
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowRulesModal(true)}
              className={`text-xs font-black flex items-center gap-1 underline transition-colors ${
                isDay 
                  ? 'text-indigo-600 hover:text-indigo-900 decoration-indigo-300' 
                  : 'text-indigo-300 hover:text-white decoration-indigo-400/50'
              }`}
            >
              <span>📋 Announce Rules to Audience</span>
            </button>
          </div>
        </div>
      )}

      {/* MAIN STADIUM ARENA BAROMETER VIEW (HIGH-CONTRAST FOR DAYLIGHT AUDITORIUM) */}
      <main className="relative z-10 flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 py-2 sm:py-3 flex flex-col justify-center my-auto pb-4">
        
        {/* 4 GIANT BAROMETER COLUMNS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-5 lg:gap-6 items-end">
          {sortedHouses.map((house, rankIdx) => {
            const score = safeScores[house?.id] || 0;
            const medals = safeMedals[house?.id] || { gold: 0, silver: 0, bronze: 0 };
            const heightPct = Math.min(100, Math.max(12, Math.round((score / maxScore) * 100)));
            const isRank1 = rankIdx === 0 && score > 0;
            const isCelebrating = celebrationHouse === house.id;

            // Day & Night Color Configurations (Vibrant, high-saturation, light-room visible)
            const tubeThemes = {
              red: {
                liquid: 'from-red-600 via-rose-500 to-amber-500',
                glow: 'shadow-red-500/40',
                dayBorder: 'border-red-500',
                nightBorder: 'border-red-500/50',
                dayBg: 'bg-red-50/60',
                nightBg: 'bg-slate-900/80',
                accentText: isDay ? 'text-red-600' : 'text-rose-400',
                scoreBg: isDay ? 'bg-red-100 text-red-700' : 'text-rose-400'
              },
              blue: {
                liquid: 'from-blue-700 via-indigo-600 to-cyan-400',
                glow: 'shadow-blue-500/40',
                dayBorder: 'border-blue-600',
                nightBorder: 'border-blue-500/50',
                dayBg: 'bg-blue-50/60',
                nightBg: 'bg-slate-900/80',
                accentText: isDay ? 'text-blue-700' : 'text-cyan-400',
                scoreBg: isDay ? 'bg-blue-100 text-blue-800' : 'text-cyan-400'
              },
              green: {
                liquid: 'from-emerald-700 via-teal-600 to-lime-400',
                glow: 'shadow-emerald-500/40',
                dayBorder: 'border-emerald-600',
                nightBorder: 'border-emerald-500/50',
                dayBg: 'bg-emerald-50/60',
                nightBg: 'bg-slate-900/80',
                accentText: isDay ? 'text-emerald-700' : 'text-emerald-400',
                scoreBg: isDay ? 'bg-emerald-100 text-emerald-800' : 'text-emerald-400'
              },
              yellow: {
                liquid: 'from-amber-600 via-yellow-400 to-amber-300',
                glow: 'shadow-amber-500/40',
                dayBorder: 'border-amber-500',
                nightBorder: 'border-amber-500/50',
                dayBg: 'bg-amber-50/60',
                nightBg: 'bg-slate-900/80',
                accentText: isDay ? 'text-amber-700' : 'text-amber-400',
                scoreBg: isDay ? 'bg-amber-100 text-amber-800' : 'text-amber-400'
              }
            }[house.id] || {
              liquid: 'from-indigo-600 to-purple-500',
              glow: 'shadow-indigo-500/40',
              dayBorder: 'border-indigo-600',
              nightBorder: 'border-indigo-500/50',
              dayBg: 'bg-indigo-50/60',
              nightBg: 'bg-slate-900/80',
              accentText: isDay ? 'text-indigo-700' : 'text-indigo-400',
              scoreBg: isDay ? 'bg-indigo-100 text-indigo-800' : 'text-indigo-400'
            };

            return (
              <motion.div
                key={house.id}
                layout
                className="flex flex-col items-center h-full justify-end relative"
              >
                {/* Crown / Rank Badge above Tube */}
                <div className="mb-1 text-center flex flex-col items-center">
                  {isRank1 ? (
                    <motion.div
                      animate={{ scale: [1, 1.1, 1], y: [0, -3, 0] }}
                      transition={{ repeat: Infinity, duration: 2 }}
                      className="px-3 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-yellow-300 text-slate-950 font-black text-[10px] sm:text-xs shadow-md shadow-amber-500/40 flex items-center gap-1 mb-0.5 ring-2 ring-amber-300"
                    >
                      <span>👑 #1 LEADER</span>
                    </motion.div>
                  ) : (
                    <span className={`text-[10px] sm:text-xs font-black mb-0.5 ${isDay ? 'text-slate-500' : 'text-slate-500'}`}>
                      Rank #{rankIdx + 1}
                    </span>
                  )}
                  
                  {/* GIANT HIGH-CONTRAST SCORE */}
                  <span className={`text-2xl sm:text-3xl lg:text-4xl font-black font-mono tracking-tight drop-shadow-sm ${tubeThemes.accentText}`}>
                    {score} <span className="text-[10px] sm:text-xs font-bold text-slate-500 dark:text-slate-400">PTS</span>
                  </span>
                </div>

                {/* THE CYLINDRICAL GLASS BAROMETER TUBE */}
                <div className={`w-full max-w-[125px] sm:max-w-[135px] h-[22vh] sm:h-[26vh] md:h-[28vh] max-h-[210px] min-h-[130px] rounded-2xl border-4 ${
                  isDay ? `${tubeThemes.dayBorder} ${tubeThemes.dayBg} shadow-xl` : `${tubeThemes.nightBorder} ${tubeThemes.nightBg} shadow-2xl`
                } relative overflow-hidden flex flex-col justify-end p-1.5 backdrop-blur-md`}>
                  
                  {/* High-Contrast Gauge Tick Marks on the Glass */}
                  <div className="absolute inset-y-0 left-2 flex flex-col justify-between py-3.5 pointer-events-none z-20">
                    <span className={`w-3.5 h-0.5 rounded-full ${isDay ? 'bg-slate-400' : 'bg-white/40'}`}></span>
                    <span className={`w-2 h-0.5 rounded-full ${isDay ? 'bg-slate-300' : 'bg-white/30'}`}></span>
                    <span className={`w-3.5 h-0.5 rounded-full ${isDay ? 'bg-slate-400' : 'bg-white/40'}`}></span>
                    <span className={`w-2 h-0.5 rounded-full ${isDay ? 'bg-slate-300' : 'bg-white/30'}`}></span>
                    <span className={`w-3.5 h-0.5 rounded-full ${isDay ? 'bg-slate-400' : 'bg-white/40'}`}></span>
                  </div>

                  {/* Liquid Fill Level */}
                  <motion.div
                    initial={{ height: '0%' }}
                    animate={{ height: `${heightPct}%` }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className={`w-full rounded-xl bg-gradient-to-t ${tubeThemes.liquid} shadow-lg ${tubeThemes.glow} relative overflow-hidden flex items-start justify-center pt-2`}
                  >
                    {/* Liquid bubbles */}
                    <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                    <div className="w-full h-1 bg-white/60 rounded-full blur-[1px]"></div>
                  </motion.div>
                </div>

                {/* House Info & Medals Pedestal at Bottom */}
                <div className={`w-full mt-2 p-2 sm:p-2.5 rounded-xl sm:rounded-2xl border transition-all text-center ${
                  isDay
                    ? `bg-white border-slate-300 shadow-md ${isRank1 ? 'ring-2 sm:ring-4 ring-amber-400 shadow-amber-300/30' : ''}`
                    : `bg-white/5 border-white/10 ${isRank1 ? 'ring-2 ring-amber-400/60 shadow-lg shadow-amber-500/10' : ''}`
                }`}>
                  <div className="flex items-center justify-center gap-1.5 mb-0.5">
                    <span className="text-lg sm:text-xl">{house.badge}</span>
                    <strong className={`text-xs sm:text-sm font-black truncate ${
                      isDay ? 'text-slate-900' : 'text-white'
                    }`}>
                      {house.name}
                    </strong>
                  </div>
                  <span className={`text-[10px] sm:text-[11px] block font-extrabold mb-1 ${
                    isDay ? 'text-slate-500' : 'text-slate-400'
                  }`}>
                    {house.house}
                  </span>

                  {/* Medals Showcase Shelf */}
                  <div className={`flex items-center justify-around text-[10px] sm:text-xs font-mono border-t pt-1 font-bold ${
                    isDay ? 'border-slate-200 text-slate-800' : 'border-white/10 text-white'
                  }`}>
                    <div title="Gold Medals">
                      <span className="text-amber-500 font-black">🥇</span> {medals.gold}
                    </div>
                    <div title="Silver Medals">
                      <span className={isDay ? "text-slate-600 font-black" : "text-slate-300 font-black"}>🥈</span> {medals.silver}
                    </div>
                    <div title="Bronze Medals">
                      <span className="text-amber-700 font-black">🥉</span> {medals.bronze}
                    </div>
                  </div>

                  {/* Direct Scoring Buttons (+10, +25, +50, -10) */}
                  <div className="grid grid-cols-4 gap-1 mt-1.5 pt-1.5 border-t border-slate-200/80 dark:border-white/10">
                    <button
                      type="button"
                      onClick={() => handleScoreBump(house.id, 10)}
                      className={`py-1 rounded-lg font-black text-[10px] sm:text-xs transition-transform active:scale-90 border shadow-sm ${
                        isDay
                          ? 'bg-emerald-50 hover:bg-emerald-500 hover:text-white text-emerald-700 border-emerald-300'
                          : 'bg-emerald-500/20 hover:bg-emerald-500 hover:text-white text-emerald-300 border-emerald-500/40'
                      }`}
                      title={`Award +10 pts to ${house.name}`}
                    >
                      +10
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScoreBump(house.id, 25)}
                      className={`py-1 rounded-lg font-black text-[10px] sm:text-xs transition-transform active:scale-90 border shadow-sm ${
                        isDay
                          ? 'bg-blue-50 hover:bg-blue-500 hover:text-white text-blue-700 border-blue-300'
                          : 'bg-blue-500/20 hover:bg-blue-500 hover:text-white text-blue-300 border-blue-500/40'
                      }`}
                      title={`Award +25 pts to ${house.name}`}
                    >
                      +25
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScoreBump(house.id, 50)}
                      className={`py-1 rounded-lg font-black text-[10px] sm:text-xs transition-transform active:scale-90 border shadow-sm ${
                        isDay
                          ? 'bg-purple-50 hover:bg-purple-500 hover:text-white text-purple-700 border-purple-300'
                          : 'bg-purple-500/20 hover:bg-purple-500 hover:text-white text-purple-300 border-purple-400/40'
                      }`}
                      title={`Award +50 pts to ${house.name}`}
                    >
                      +50
                    </button>
                    <button
                      type="button"
                      onClick={() => handleScoreBump(house.id, -10)}
                      className={`py-1 rounded-lg font-black text-[10px] sm:text-xs transition-transform active:scale-90 border shadow-sm ${
                        isDay
                          ? 'bg-rose-50 hover:bg-rose-500 hover:text-white text-rose-700 border-rose-300'
                          : 'bg-rose-500/20 hover:bg-rose-500 hover:text-white text-rose-300 border-rose-500/40'
                      }`}
                      title={`Deduct 10 pts from ${house.name}`}
                    >
                      -10
                    </button>
                  </div>
                </div>

              </motion.div>
            );
          })}
        </div>

      </main>

      {/* Bottom Live Victory Ticker */}
      <footer className={`relative z-10 px-6 py-3 border-t backdrop-blur-md flex items-center justify-between text-xs transition-colors ${
        isDay 
          ? 'bg-white border-slate-200 text-slate-800 shadow-inner' 
          : 'bg-slate-950/80 border-white/10 text-slate-300'
      }`}>
        <div className="flex items-center gap-3 w-full overflow-hidden">
          <span className="px-2.5 py-1 rounded-md bg-amber-500 text-slate-950 font-black uppercase text-[10px] tracking-wider shrink-0 flex items-center gap-1 shadow-sm">
            <SafeIcon icon={FiAward} /> LATEST SPORTS RESULT:
          </span>
          {latestEvent ? (
            <div className="truncate font-semibold text-xs sm:text-sm">
              <strong className={isDay ? "text-amber-600 font-black" : "text-amber-400 font-black"}>
                {latestEvent.place === '1st' ? '🥇 1st Place' : latestEvent.place === '2nd' ? '🥈 2nd Place' : '🥉 3rd Place'}
              </strong> in <strong className={isDay ? "text-slate-900 font-black" : "text-white font-black"}>{latestEvent.sportName}</strong> ({latestEvent.division}) awarded to <strong className={isDay ? "text-emerald-700 font-black" : "text-emerald-400 font-black"}>{latestEvent.houseName}</strong> (+{latestEvent.points} pts) &bull; Athlete: <span className="font-bold">{latestEvent.studentName}</span>
            </div>
          ) : (
            <span className={isDay ? "text-slate-500 italic" : "text-slate-500 italic"}>
              No sports events logged yet. Matches will appear here live as they conclude!
            </span>
          )}
        </div>
      </footer>

      {/* INLINE EDIT SCHOOL / ORGANIZER NAME MODAL (ACCESSIBLE RIGHT ON PROJECTOR VIEW) */}
      <AnimatePresence>
        {isEditingSchoolName && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-md flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.9, y: 15 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 15 }}
              className="bg-white dark:bg-slate-900 border-2 border-indigo-500/50 rounded-3xl max-w-lg w-full p-6 shadow-2xl relative"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                  <span>🏫 Edit School / Event Organizer Name</span>
                </h3>
                <button
                  type="button"
                  onClick={() => setIsEditingSchoolName(false)}
                  className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white"
                >
                  <SafeIcon icon={FiX} className="text-lg" />
                </button>
              </div>

              <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
                This name is displayed prominently at the top of the stadium projector view for the entire auditorium to see.
              </p>

              <form onSubmit={handleSaveSchoolName} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-600 dark:text-slate-300 uppercase mb-1">
                    School / Organizer Title
                  </label>
                  <input
                    type="text"
                    value={schoolNameInput}
                    onChange={e => setSchoolNameInput(e.target.value)}
                    placeholder="e.g. St. Xavier's High School Annual Sports Meet"
                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-indigo-400 rounded-xl px-4 py-2.5 text-base font-black text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                    autoFocus
                  />
                </div>

                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <button
                    type="button"
                    onClick={() => setIsEditingSchoolName(false)}
                    className="px-4 py-2 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-xl font-bold text-xs"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-slate-950 rounded-xl font-black text-xs shadow-md"
                  >
                    Save & Update Screen
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* GAME RULES BRIEFING FULL-SCREEN OVERLAY FOR AUDIENCE & KIDS */}
      <AnimatePresence>
        {showRulesModal && activeSportEvent && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/90 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-slate-900 border-2 border-indigo-500/40 rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative overflow-hidden"
            >
              {/* Header Glow */}
              <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-amber-400 via-rose-500 to-indigo-500"></div>

              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <span className="px-3 py-1 rounded-full bg-amber-400/20 text-amber-300 border border-amber-400/30 text-xs font-black uppercase tracking-wider">
                    📋 Official Match Rules & Briefing
                  </span>
                  <h2 className="text-2xl sm:text-3xl font-black text-white mt-2 flex items-center gap-2">
                    <span>{activeSportEvent.sportName}</span>
                  </h2>
                  <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                    <span>Division: <strong className="text-slate-200">{activeSportEvent.division}</strong></span>
                    <span>&bull;</span>
                    <span>Category: <strong className="text-slate-200">{activeSportEvent.category}</strong></span>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowRulesModal(false)}
                  className="p-2 rounded-xl bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              {/* Game Objective */}
              {activeSportEvent.objective && (
                <div className="mb-6 p-4 rounded-2xl bg-indigo-500/10 border border-indigo-500/20">
                  <div className="text-xs font-black uppercase tracking-wider text-indigo-300 flex items-center gap-1.5 mb-1">
                    <SafeIcon icon={FiTarget} /> Game Objective
                  </div>
                  <p className="text-sm sm:text-base font-semibold text-white">
                    {activeSportEvent.objective}
                  </p>
                </div>
              )}

              {/* Rules List */}
              <div className="mb-6">
                <div className="text-xs font-black uppercase tracking-wider text-amber-400 mb-3 flex items-center gap-1.5">
                  <SafeIcon icon={FiCheckCircle} /> Rules of Play:
                </div>
                {activeSportEvent.rules && activeSportEvent.rules.length > 0 ? (
                  <div className="space-y-2.5">
                    {activeSportEvent.rules.map((rule, idx) => (
                      <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-white/5 border border-white/5">
                        <span className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30 font-bold text-xs flex items-center justify-center shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm text-slate-200 font-medium leading-relaxed">
                          {rule}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-400 italic">
                    Follow standard tournament rules and referee signals. Play fair and cheer your house!
                  </p>
                )}
              </div>

              {/* Footer Actions */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <span className="text-xs text-slate-400 font-bold">
                  🏫 {organizerName}
                </span>
                <button
                  type="button"
                  onClick={() => setShowRulesModal(false)}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-400 text-slate-950 font-black text-sm shadow-lg shadow-amber-500/20 hover:brightness-110 active:scale-95 transition-all"
                >
                  Ready to Start!
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* INTER-HOUSE CHAMPION ANNOUNCEMENT MODAL */}
      <AnimatePresence>
        {showChampionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-xl flex items-center justify-center p-4 sm:p-6 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="bg-white dark:bg-slate-900 border-2 border-amber-500/50 rounded-3xl max-w-2xl w-full p-6 sm:p-8 shadow-2xl relative overflow-hidden my-8"
            >
              {/* Header Gold Bar */}
              <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 via-yellow-300 to-amber-500"></div>

              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2.5 py-0.5 rounded-full bg-amber-500/20 text-amber-600 dark:text-amber-400 border border-amber-500/30 text-[10px] font-black uppercase tracking-wider">
                      Official Inter-House Winner
                    </span>
                    <span className="text-xs text-slate-400 font-bold">&bull; 🏫 {organizerName}</span>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white flex items-center gap-2">
                    <span>🏆 Announce Inter-House Champion</span>
                  </h2>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    Crowning a house adds them to the <strong>Inter-House Champions</strong> Roll of Honor and logs the award into <strong>Sports Day Log & Match History</strong> for prize distribution.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => setShowChampionModal(false)}
                  className="p-2 rounded-xl text-slate-400 hover:text-slate-700 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                >
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>

              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                {/* 1. SELECT WINNING HOUSE */}
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                    1. Select Champion House:
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                    {sortedHouses.map((house, idx) => {
                      const isSelected = (selectedChampionHouseId || sortedHouses[0]?.id) === house.id;
                      const houseScore = safeScores[house.id] || 0;
                      const houseThemes = {
                        red: 'border-red-500 bg-red-500/10 text-red-700 dark:text-red-300',
                        blue: 'border-blue-500 bg-blue-500/10 text-blue-700 dark:text-blue-300',
                        green: 'border-emerald-500 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
                        yellow: 'border-amber-500 bg-amber-500/10 text-amber-700 dark:text-amber-300'
                      }[house.id] || 'border-indigo-500 bg-indigo-500/10';

                      return (
                        <button
                          key={house.id}
                          type="button"
                          onClick={() => setSelectedChampionHouseId(house.id)}
                          className={`p-3 rounded-2xl border-2 text-center transition-all cursor-pointer relative ${
                            isSelected
                              ? `${houseThemes} ring-2 ring-amber-400 shadow-md`
                              : 'border-slate-200 dark:border-slate-700 hover:border-slate-400 dark:hover:border-slate-600 bg-slate-50 dark:bg-slate-800/50'
                          }`}
                        >
                          {idx === 0 && (
                            <span className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.2 rounded-full bg-amber-400 text-slate-950 text-[9px] font-black uppercase shadow-sm">
                              Leader
                            </span>
                          )}
                          <div className="text-2xl mb-1">{house.badge || '🏆'}</div>
                          <div className="text-xs font-black truncate">{house.name}</div>
                          <div className="text-xs font-mono font-bold mt-1 text-slate-500 dark:text-slate-400">
                            {houseScore} pts
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 2. AWARD TYPE */}
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                    2. Championship Award / Trophy Title:
                  </label>
                  <select
                    value={awardType}
                    onChange={e => setAwardType(e.target.value)}
                    className="w-full bg-slate-50 dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 rounded-xl px-3.5 py-2.5 text-xs sm:text-sm font-bold text-slate-900 dark:text-white outline-none focus:border-amber-500"
                  >
                    <option value="🏆 Overall Annual Inter-House Championship Trophy">🏆 Overall Annual Inter-House Championship Trophy</option>
                    <option value="🥇 Sports Day Inter-House Gold Cup">🥇 Sports Day Inter-House Gold Cup</option>
                    <option value="🌟 General Championship Shield">🌟 General Championship Shield</option>
                    <option value="🎯 Inter-House Quiz & Cultural Trophy">🎯 Inter-House Quiz & Cultural Trophy</option>
                    <option value="⚡ Outstanding House Spirit & March Past Trophy">⚡ Outstanding House Spirit & March Past Trophy</option>
                    <option value="🎖️ Academic & STEM Excellence Shield">🎖️ Academic & STEM Excellence Shield</option>
                    <option value="CUSTOM">✏️ Custom Trophy / Award Name...</option>
                  </select>

                  {awardType === 'CUSTOM' && (
                    <input
                      type="text"
                      placeholder="Type custom award name..."
                      value={customAward}
                      onChange={e => setCustomAward(e.target.value)}
                      className="mt-2 w-full bg-slate-50 dark:bg-slate-800 border-2 border-amber-400 rounded-xl px-3.5 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none"
                      autoFocus
                    />
                  )}
                </div>

                {/* 3. EVENT SOURCE & CATEGORY */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                      Event Source / Tournament:
                    </label>
                    <input
                      type="text"
                      value={eventSource}
                      onChange={e => setEventSource(e.target.value)}
                      placeholder="e.g. Annual Sports & Quiz Meet"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none focus:border-amber-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                      Announced By (Authority):
                    </label>
                    <input
                      type="text"
                      value={announcedBy}
                      onChange={e => setAnnouncedBy(e.target.value)}
                      placeholder="e.g. Chief Sports Referee / Principal"
                      className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3 py-2 text-xs font-bold text-slate-900 dark:text-white outline-none focus:border-amber-500"
                    />
                  </div>
                </div>

                {/* 4. PRIZE DISTRIBUTION INFO */}
                <div>
                  <label className="block text-xs font-black uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">
                    Prize Distribution Information:
                  </label>
                  <textarea
                    rows={2}
                    value={prizeDistributionInfo}
                    onChange={e => setPrizeDistributionInfo(e.target.value)}
                    placeholder="Details regarding medals, trophies, and student distribution on stage..."
                    className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-600 rounded-xl px-3.5 py-2 text-xs font-medium text-slate-900 dark:text-white outline-none focus:border-amber-500"
                  />
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    This information appears in the Sports Day Log & Match History for coordination during the closing ceremony.
                  </p>
                </div>
              </div>

              {/* MODAL FOOTER */}
              <div className="flex items-center justify-between pt-5 mt-4 border-t border-slate-200 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setShowChampionModal(false)}
                  className="px-4 py-2.5 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-bold text-xs hover:bg-slate-200"
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleAnnounceChampion}
                  className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 text-slate-950 font-black text-xs sm:text-sm shadow-xl shadow-amber-500/25 hover:brightness-110 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <SafeIcon icon={FiAward} className="text-base" />
                  <span>Announce & Crown Champions 🏆</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}

class StageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.warn("LiveHouseBarometerStage error caught by boundary:", error, errorInfo);
  }
  handleReset = () => {
    try {
      localStorage.removeItem('gamifycenter_house_scores');
      localStorage.removeItem('gamifycenter_house_medals');
      localStorage.removeItem('gamifycenter_active_sport_event');
    } catch (e) {}
    window.location.reload();
  };
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-white flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-3xl mb-4 border border-amber-500/30">
            🏆
          </div>
          <h2 className="text-2xl font-black text-white mb-2">Inter-House Championship Barometer</h2>
          <p className="text-slate-400 text-sm max-w-md mb-6">
            Display loaded successfully. Click below to refresh the live stage scoreboards.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-sm"
            >
              Reload Stage
            </button>
            <button
              onClick={this.handleReset}
              className="px-5 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-sm border border-slate-700"
            >
              Reset Cache & Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function LiveHouseBarometerStage() {
  return (
    <StageErrorBoundary>
      <LiveHouseBarometerStageComponent />
    </StageErrorBoundary>
  );
}


